/// @ts-check
import '../../../styl/src/annual-report.scss';

import { animate, stagger } from 'motion';

window.addEventListener('load', startup, false);

function startup() {
	const shouldReduceMotion = window.matchMedia(
		'(prefers-reduced-motion)'
	).matches;

	if (!shouldReduceMotion) {
		initReportAnimations();
	}
}

function initReportAnimations() {
	// Get all the animated sections on the page
	/** @type {NodeListOf<HTMLElement>} */
	const animatedSections = document.querySelectorAll('section[data-animation]');

	for (const section of animatedSections) {
		initAnimatedSection(section);
	}

	// Init IntersectionObserver for animated sections
	const animationsObserver = new IntersectionObserver(
		(entries) => {
			for (const entry of entries) {
				if (entry.isIntersecting && entry.target instanceof HTMLElement) {
					animateSection(entry.target);
				}
			}
		},
		{
			root: null,
			rootMargin: '0px 0px -75% 0px', // top quarter of the viewport
		}
	);

	for (const section of animatedSections) {
		animationsObserver.observe(section);
	}
}

/**
 * Get the animated elements for the given section
 *
 * @param {HTMLElement} parentElement
 * @returns elements
 */
function getSectionElements(parentElement) {
	/** @type {Array<HTMLElement | SVGElement>} */
	let elements = [];

	switch (parentElement.dataset.animation) {
		case 'donut':
			/** @type {NodeListOf<SVGPathElement>} */
			const paths = parentElement.querySelectorAll(
				'.chart-layout--donut svg > path'
			);
			/** @type {NodeListOf<SVGCircleElement>} */
			const circles = parentElement.querySelectorAll(
				'.chart-layout--donut svg > circle'
			);

			elements = [...paths, ...circles];
			break;

		case 'waffle':
			/** @type {NodeListOf<SVGRectElement | SVGPathElement | SVGGElement>} */
			const waffleBoxes = parentElement.querySelectorAll(
				'.chart-layout--waffle svg rect, .chart-layout--waffle svg path, .chart-layout--waffle svg g'
			);

			elements = [...waffleBoxes];
			break;

		case 'icons-fade-in':
			/** @type {NodeListOf<HTMLDivElement>} */
			const fadeIcons = parentElement.querySelectorAll(
				'.illustrated-stat__illustration'
			);

			elements = [...fadeIcons];
			break;

		default:
			break;
	}

	return elements;
}

/**
 * Initialize the animated elements of the given section
 *
 * @param {HTMLElement} parentElement
 */
function initAnimatedSection(parentElement) {
	const animatedElements = getSectionElements(parentElement);

	// We want to hide the elements before animating them
	switch (parentElement.dataset.animation) {
		case 'donut':
			for (const path of animatedElements) {
				path.style.visibility = 'hidden';
				path.style.strokeDasharray = '1';
				path.style.strokeDashoffset = '1';
			}
			break;

		case 'waffle':
			for (const box of animatedElements) {
				box.style.opacity = '0';
				box.style.transform = 'translateY(55px)';
			}
			break;

		case 'icons-fade-in':
			for (const icon of animatedElements) {
				icon.style.opacity = '0';
				icon.style.transform = 'translateY(250px)';
			}
			break;

		default:
			break;
	}
}

/**
 * Animate the animated elements of the given section
 *
 * @param {HTMLElement} parentElement
 */
function animateSection(parentElement) {
	const animatedElements = getSectionElements(parentElement);

	switch (parentElement.dataset.animation) {
		case 'donut':
			animate(animatedElements, drawStroke(1), {
				duration: 1,
				easing: 'ease-out',
				delay: stagger(0.25),
			});
			break;

		case 'waffle':
			animate(
				animatedElements,
				{ opacity: 1, y: 0 },
				{
					duration: 0.05,
					easing: 'ease-in-out',
					delay: stagger(0.025),
				}
			);
			break;

		case 'icons-fade-in':
			animate(
				animatedElements,
				{ opacity: 1, y: 0 },
				{
					duration: 0.35,
					delay: stagger(0.25),
					easing: 'ease-in-out',
				}
			);
			break;

		default:
			break;
	}
}

/**
 * Draw SVG stroke
 *
 * @see https://motion.dev/examples/path-drawing
 *
 * @param {number} progress
 * @returns newPath
 */
function drawStroke(progress) {
	return {
		strokeDashoffset: 1 - progress,
		visibility: 'visible',
	};
}
